<template>
  <messenger-view-layout
    :title="viewSettings.channel.name"
    :channel-type="viewSettings.channel.type"
    @back="$emit('close')"
    @resize="$emit('resize', $event)"
  >
    <div class="view-content">
      <div class="view-content__message view-message">
        <img
          class="view-message__image"
          :src="SuccessImageUrl"
          alt="success"
        />
        <div class="view-message__title">
          <messenger-text-with-emoji
            :text="viewSettings.title"
            emoji-size="20px"
          />
        </div>
        <div class="view-message__description">
          <span v-safe-html="successMessage" />
        </div>
      </div>

      <div class="view-content__buttons view-buttons">
        <widget-button
          v-bind="themeButtonProps"
          class="view-buttons__button"
          @click="$emit('close')"
        >
          {{ $t('messengerWidget.closeThisWindow') }}
        </widget-button>

        <widget-button
          v-bind="themeButtonProps"
          class="view-buttons__button"
          view="outline"
          @click="$emit('start-over')"
        >
          {{ $t('messengerWidget.startOver') }}
        </widget-button>
      </div>
    </div>

    <template v-slot:alert>
      <slot name="alert" />
    </template>
  </messenger-view-layout>
</template>

<script setup lang="ts">
import type { MessengerSuccessViewSettings } from '@widgets/messenger/types'
import MessengerViewLayout from '@widgets/messenger/components/MessengerViewLayout.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import SuccessImageUrl from '@/assets/images/success.svg'
import { computed } from '@/composition/vue/compositionApi'
import { createContentFromTemplate } from '@/services/forms/tmTiptap/utils/createContentFromTemplate'

const props = defineProps<{
  viewSettings: MessengerSuccessViewSettings
}>()

defineEmits<{
  (e: 'close'): void
  (e: 'start-over'): void
  (e: 'resize', height: number): void
}>()

const { themeButtonProps } = useWidgetTheme()

const successMessage = computed(() => {
  return createContentFromTemplate(props.viewSettings.description, false)
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.view-content {
  padding: 112px 24px;

  &__message {
    margin-bottom: 48px;
  }
}

.view-message {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__image {
    margin-bottom: 16px;
  }

  &__title {
    @include truncate(3);
    color: $emphasize;
    font-size: 20px;
    font-weight: $bold-font;
    line-height: 24px;
    margin-bottom: 8px;
    text-align: center;
  }

  &__description {
    @include truncate(3);
    color: $neutral;
    font-size: 14px;
    font-weight: $regular-font;
    line-height: 19px;
    text-align: center;
    pointer-events: auto;
    max-width: 100%;
    word-break: break-word;
  }
}

.view-buttons {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;

  &__button {
    margin: 0 !important;
  }
}
</style>
